import React from 'react';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import DrawLifecycleMessage from './DrawLifecycleMessage';
import { DRAW_LIFECYCLE_STATES } from '../utils/drawLifecycle';
import { useTranslations } from '../contexts/TranslationContext';

/**
 * Countdown style catalog (v2).
 *
 * Rendered via <CountdownTimerStyles style={name} variant="dark|light|auto" ... />.
 *
 * `variant` controls contrast palette:
 *   - 'auto'  (default) — Tailwind `dark:` classes, respects system theme.
 *   - 'dark'  — assumes a dark hero background. Digits white, labels white/70, tiles glass.
 *   - 'light' — assumes a light hero background. Digits near-black, labels gray.
 *
 * Accent color picks up `var(--charity-primary)` when available so every style
 * reacts to the active charity theme.
 *
 * Catalog (7 styles):
 *   - GRID        — 4-up tile grid (default)
 *   - CLASSIC     — inline, colon-separated, clock-icon prefix
 *   - FLIP        — flip-card tiles, bare
 *   - FLIP_DARK   — flip-card tiles only (dark panels, white digits, mid-crease); no outer
 *                   wrapper — host provides chrome. DAYS tile hidden once days hits 0
 *   - PULSE       — circular digits with pulsing charity-colored ring
 *   - MINIMAL_LUX — typography-first; thin uppercase labels, large light-weight digits, hairline separators
 *   - TICKER      — LED stock-ticker board; glowing monospaced digits on dark strip
 *
 * Unknown / legacy style keys (e.g. 'neon', 'jackpot', 'slots', 'glow') fall back to GRID silently.
 */
export const CountdownStyles = {
  GRID: 'grid',
  CLASSIC: 'classic',
  FLIP: 'flip',
  FLIP_DARK: 'flip-dark',
  PULSE: 'pulse',
  MINIMAL_LUX: 'minimal-lux',
  TICKER: 'ticker',
};

// ---------- palette helpers ----------
function palette(variant) {
  if (variant === 'dark') {
    return {
      digit: 'text-white',
      label: 'text-white/70',
      sep: 'text-white/50',
      tile: 'bg-white/10 border border-white/20 backdrop-blur-sm',
      tileAccent: 'bg-white/15 border border-white/30 backdrop-blur-sm',
      crease: 'bg-black/30',
      accentVar: 'var(--charity-primary, #3b82f6)',
    };
  }
  if (variant === 'light') {
    return {
      digit: 'text-gray-900',
      label: 'text-gray-500',
      sep: 'text-gray-400',
      tile: 'bg-white/80 border border-gray-200 shadow-sm',
      tileAccent: 'bg-white border border-gray-300 shadow',
      crease: 'bg-gray-300',
      accentVar: 'var(--charity-primary, #3b82f6)',
    };
  }
  return {
    digit: 'text-gray-900 dark:text-white',
    label: 'text-gray-500 dark:text-gray-400',
    sep: 'text-gray-400 dark:text-gray-500',
    tile: 'bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30',
    tileAccent: 'bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/30 dark:to-amber-900/30',
    crease: 'bg-gray-300 dark:bg-gray-950',
    accentVar: 'var(--charity-primary, #3b82f6)',
  };
}

// ---------- GRID (default) ----------
function GridCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const p = palette(variant);
  const items = [
    { val: timeLeft.days, label: labels.days },
    { val: timeLeft.hours, label: labels.hours },
    { val: timeLeft.minutes, label: labels.minutes },
    { val: timeLeft.seconds, label: labels.seconds },
  ];
  return (
    <div className="grid grid-cols-4 gap-2 w-full">
      {items.map((it, i) => {
        const isLast = i === items.length - 1;
        return (
          <div key={i} className="text-center">
            <div className={`${isLast ? p.tileAccent : p.tile} rounded-xl p-3 transition-transform hover:scale-105`}>
              <div
                className={`text-3xl font-bold ${p.digit} ${shouldAnimate ? 'animate-pulse' : ''}`}
                style={{ color: p.accentVar }}
              >
                {String(it.val).padStart(2, '0')}
              </div>
              <div className={`text-xs ${p.label} uppercase mt-1 tracking-wider`}>{it.label}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------- CLASSIC (inline) ----------
function ClassicCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const p = palette(variant);
  const digit = `text-2xl font-bold ${p.digit} transition-all ${shouldAnimate ? 'scale-110' : 'scale-100'}`;
  return (
    <div className="flex items-center gap-3">
      <Clock className="w-5 h-5" style={{ color: p.accentVar }} />
      <div className="flex items-center gap-1 font-mono">
        <span className={digit}>{String(timeLeft.days).padStart(2, '0')}</span>
        <span className={`text-xl font-bold ${p.label}`}>d</span>
        <span className={`text-xl font-bold ${p.sep} animate-pulse`}>:</span>
        <span className={digit}>{String(timeLeft.hours).padStart(2, '0')}</span>
        <span className={`text-xl font-bold ${p.sep} animate-pulse`}>:</span>
        <span className={digit}>{String(timeLeft.minutes).padStart(2, '0')}</span>
        <span className={`text-xl font-bold ${p.sep} animate-pulse`}>:</span>
        <span className={digit}>{String(timeLeft.seconds).padStart(2, '0')}</span>
      </div>
    </div>
  );
}

// ---------- FLIP ----------
function FlipCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const p = palette(variant);
  const cardBg =
    variant === 'dark'
      ? 'bg-gradient-to-b from-white/15 to-white/5 border border-white/20'
      : variant === 'light'
      ? 'bg-gradient-to-b from-white to-gray-100 border border-gray-300 shadow-xl'
      : 'bg-gradient-to-b from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 border-2 border-gray-300 dark:border-gray-700 shadow-xl';

  const FlipCard = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-20 mb-1">
        <div
          className={`absolute inset-0 ${cardBg} rounded-lg flex items-center justify-center transition-transform ${
            shouldAnimate ? 'scale-105' : 'scale-100'
          }`}
        >
          <span className={`text-3xl font-bold ${p.digit}`}>{String(value).padStart(2, '0')}</span>
        </div>
        <div className={`absolute inset-x-0 top-1/2 h-px ${p.crease}`} />
      </div>
      <span className={`text-xs uppercase tracking-wider ${p.label}`}>{label}</span>
    </div>
  );

  return (
    <div className="flex items-center gap-3">
      <FlipCard value={timeLeft.days} label={labels.days} />
      <span className={`text-2xl font-bold ${p.sep} animate-pulse`}>:</span>
      <FlipCard value={timeLeft.hours} label={labels.hours} />
      <span className={`text-2xl font-bold ${p.sep} animate-pulse`}>:</span>
      <FlipCard value={timeLeft.minutes} label={labels.minutes} />
      <span className={`text-2xl font-bold ${p.sep} animate-pulse`}>:</span>
      <FlipCard value={timeLeft.seconds} label={labels.seconds} />
    </div>
  );
}

// ---------- FLIP DARK ----------
// Tiles-only: dark panel tiles with horizontal mid-crease, white digits,
// small uppercase labels. No outer container, no header — hosts provide chrome.
// DAYS tile auto-hides when days === 0.
function FlipDarkCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const tileCls = 'bg-gradient-to-b from-slate-800 to-slate-900 border border-white/10';
  const digitCls = 'text-white';
  const labelCls = 'text-white/60';

  const FlipTile = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="relative w-16 h-20 sm:w-20 sm:h-24">
        <div
          className={`absolute inset-0 ${tileCls} rounded-lg flex items-center justify-center transition-transform ${
            shouldAnimate ? 'scale-[1.03]' : 'scale-100'
          }`}
        >
          <span className={`text-3xl sm:text-4xl font-bold tabular-nums ${digitCls}`}>
            {String(value).padStart(2, '0')}
          </span>
        </div>
        {/* horizontal mid-crease */}
        <div className="absolute inset-x-0 top-1/2 h-px bg-black/60" />
      </div>
      <span className={`text-[10px] sm:text-xs uppercase tracking-[0.18em] mt-2 ${labelCls}`}>
        {label}
      </span>
    </div>
  );

  const Colon = () => (
    <span className="text-2xl sm:text-3xl font-bold text-white/50 pb-6 select-none">:</span>
  );

  const showDays = timeLeft.days > 0;

  return (
    <div className="flex items-end gap-2 sm:gap-3">
      {showDays && (
        <>
          <FlipTile value={timeLeft.days} label={labels.days} />
          <Colon />
        </>
      )}
      <FlipTile value={timeLeft.hours} label={labels.hours} />
      <Colon />
      <FlipTile value={timeLeft.minutes} label={labels.minutes} />
      <Colon />
      <FlipTile value={timeLeft.seconds} label={labels.seconds} />
    </div>
  );
}

// ---------- PULSE ----------
// Circular digit plates with a pulsing charity-colored ring. Modern, gaming-ish.
function PulseCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const p = palette(variant);
  const accent = p.accentVar;

  const ringBase =
    variant === 'light'
      ? 'bg-white'
      : variant === 'dark'
      ? 'bg-white/5 backdrop-blur-sm'
      : 'bg-white dark:bg-slate-900';

  const Ring = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div
        className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-full ${ringBase} flex items-center justify-center transition-transform ${
          shouldAnimate ? 'scale-[1.04]' : 'scale-100'
        }`}
        style={{
          boxShadow: shouldAnimate
            ? `0 0 0 2px ${accent}, 0 0 22px 2px ${accent}66`
            : `0 0 0 2px ${accent}99, 0 0 14px 0 ${accent}33`,
        }}
      >
        <span className={`text-2xl sm:text-3xl font-bold tabular-nums ${p.digit}`}>
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className={`text-[10px] sm:text-xs uppercase tracking-[0.18em] ${p.label} mt-2 font-semibold`}>
        {label}
      </span>
    </div>
  );

  return (
    <div className="flex items-center gap-3 sm:gap-4">
      <Ring value={timeLeft.days} label={labels.days} />
      <Ring value={timeLeft.hours} label={labels.hours} />
      <Ring value={timeLeft.minutes} label={labels.minutes} />
      <Ring value={timeLeft.seconds} label={labels.seconds} />
    </div>
  );
}

// ---------- MINIMAL LUX ----------
// Typography-first: very light-weight digits, thin uppercase labels,
// single hairline separators — no boxes, no chrome.
function MinimalLuxCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const p = palette(variant);
  const accent = p.accentVar;
  const hairline =
    variant === 'dark'
      ? 'bg-white/25'
      : variant === 'light'
      ? 'bg-gray-300'
      : 'bg-gray-300 dark:bg-white/25';

  const Unit = ({ value, label, isLast }) => (
    <div className="flex flex-col items-center">
      <span
        className={`text-4xl sm:text-5xl font-extralight tabular-nums leading-none ${p.digit} transition-all ${
          isLast && shouldAnimate ? 'opacity-80' : 'opacity-100'
        }`}
        style={isLast ? { color: accent } : undefined}
      >
        {String(value).padStart(2, '0')}
      </span>
      <span className={`mt-2 text-[10px] uppercase tracking-[0.32em] ${p.label}`}>
        {label}
      </span>
    </div>
  );

  const Sep = () => <div className={`w-px h-8 sm:h-10 ${hairline} mx-3 sm:mx-5`} />;

  return (
    <div className="flex items-center">
      <Unit value={timeLeft.days} label={labels.days} />
      <Sep />
      <Unit value={timeLeft.hours} label={labels.hours} />
      <Sep />
      <Unit value={timeLeft.minutes} label={labels.minutes} />
      <Sep />
      <Unit value={timeLeft.seconds} label={labels.seconds} isLast />
    </div>
  );
}

// ---------- TICKER ----------
// LED stock-ticker board: dark strip, glowing monospaced charity-colored digits,
// blinking colons. Feels like a trading desk / sportsbook board.
function TickerCountdown({ timeLeft, shouldAnimate, variant, labels }) {
  const accent = 'var(--charity-primary, #3b82f6)';
  const stripCls =
    variant === 'light'
      ? 'bg-slate-900 border border-slate-800'
      : 'bg-black/80 border border-white/10 backdrop-blur-sm';
  const labelCls = 'text-white/50';

  const digitStyle = {
    color: accent,
    textShadow: `0 0 6px ${accent}, 0 0 14px ${accent}80`,
  };

  const Cell = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <span
        className={`font-mono font-bold tabular-nums text-3xl sm:text-4xl leading-none transition-transform ${
          shouldAnimate ? 'scale-[1.04]' : 'scale-100'
        }`}
        style={digitStyle}
      >
        {String(value).padStart(2, '0')}
      </span>
      <span className={`mt-1.5 text-[9px] sm:text-[10px] uppercase tracking-[0.22em] ${labelCls}`}>
        {label}
      </span>
    </div>
  );

  const Colon = () => (
    <span
      className="font-mono font-bold text-3xl sm:text-4xl leading-none animate-pulse pb-4"
      style={digitStyle}
    >
      :
    </span>
  );

  return (
    <div className={`inline-flex items-end gap-2 sm:gap-3 ${stripCls} rounded-xl px-4 py-3 sm:px-5 sm:py-3.5 shadow-lg`}>
      <Cell value={timeLeft.days} label={labels.days} />
      <Colon />
      <Cell value={timeLeft.hours} label={labels.hours} />
      <Colon />
      <Cell value={timeLeft.minutes} label={labels.minutes} />
      <Colon />
      <Cell value={timeLeft.seconds} label={labels.seconds} />
    </div>
  );
}

// ---------- dispatcher ----------
export default function CountdownTimerStyles({
  timeLeft,
  shouldAnimate,
  style = CountdownStyles.GRID,
  variant = 'auto',
}) {
  const { t } = useTranslations();
  const labels = {
    days: t('countdown.days') || 'Days',
    hours: t('countdown.hours') || 'Hrs',
    minutes: t('countdown.minutes') || 'Min',
    seconds: t('countdown.seconds') || 'Sec',
  };

  if (timeLeft?.isComplete) {
    return <DrawLifecycleMessage state={DRAW_LIFECYCLE_STATES.EXPIRED} compact />;
  }

  switch (style) {
    case CountdownStyles.CLASSIC:
      return <ClassicCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
    case CountdownStyles.FLIP:
      return <FlipCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
    case CountdownStyles.FLIP_DARK:
      return <FlipDarkCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
    case CountdownStyles.PULSE:
      return <PulseCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
    case CountdownStyles.MINIMAL_LUX:
      return <MinimalLuxCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
    case CountdownStyles.TICKER:
      return <TickerCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
    case CountdownStyles.GRID:
    default:
      // Legacy keys ('neon', 'jackpot', 'slots', 'glow', etc.) fall through to GRID silently.
      return <GridCountdown timeLeft={timeLeft} shouldAnimate={shouldAnimate} variant={variant} labels={labels} />;
  }
}
